import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { FormControl } from '@angular/forms'
import { Router } from '@angular/router'
import { CartItemComponentOptions, PromotionLocation, PromotionResult } from '@spartacus/cart/base/root'
import { CurrentProductService } from '@spartacus/storefront'
import { Observable, Subscription, map } from 'rxjs'
import { BaseCountry } from '../../../../models/enum/country.enum'
import { StockStatus } from '../../../../models/enum/stock.enum'
import { ICustomOrderEntry } from '../../../../models/interface/order-entry.interface'
import { ICustomProduct } from '../../../../models/interface/product.interface'
import { UomPallets } from '../../../../models/uom.model'
import { ProductDetailsService } from '../../../../services/product.service'
import { SharedService } from '../../../../services/shared/shared.service'

@Component({
  selector: 'app-custom-cart-item',
  templateUrl: './custom-cart-item.component.html',
  styleUrls: ['./custom-cart-item.component.scss'],
  standalone: false
})
export class CustomCartItemComponent implements OnInit, OnDestroy {
  private sub: Subscription = new Subscription()
  public fullPallet$: Observable<UomPallets>
  public isFullPallet: boolean = false
  public isFullLayer: boolean = false

  @Input() showAllStatus?: boolean
  @Input() showCancel?: boolean
  @Input() showSpinner?: boolean
  @Input() isCancelling?: boolean
  @Input() compact = false
  @Input() item: ICustomOrderEntry
  @Input() readonly = false
  @Input() uomCode = null
  @Input() uomName = null
  @Input() hasStock = false
  @Input() maxQuantity?: number
  @Input() quantityControl: FormControl
  @Output() itemRemoved?: EventEmitter<boolean> = new EventEmitter()
  @Output() itemCancel?: EventEmitter<void> = new EventEmitter()

  @Input() promotionLocation: PromotionLocation = PromotionLocation.ActiveCart

  // TODO: evaluate whether this is generic enough
  @Input() options: CartItemComponentOptions = {
    isSaveForLater: false,
    optionalBtn: null
  }

  @Input() set validateQuantity(validate: boolean) {
    if (validate) {
      if (this.item?.product?.stock?.stockLevelStatus === StockStatus.inStock) {
        if (this.quantityControl) {
          this.sub.add(
            this.quantityControl.valueChanges.subscribe(() => {
              this.sharedService.disableSimulate = false
            })
          )
        }
      }
    }
  }

  quantity = 1
  appliedProductPromotions$: Observable<PromotionResult[]>
  subscription: Subscription

  constructor(
    protected currentProductService: CurrentProductService,
    protected sharedService: SharedService,
    private productDetailsService: ProductDetailsService,
    private router: Router
  ) {}

  ngOnInit(): void {
    if (this.quantityControl) {
      this.sub.add(
        this.quantityControl.valueChanges.subscribe(() => {
          this.sharedService.disableSimulate = false
        })
      )
    }
    if (this.quantityControl.value > this.maxQuantity) {
      this.quantityControl.setValue(this.maxQuantity)
    }

    this.setFullPallet()
  }

  setFullPallet(): void {
    const countryPallet = this.sharedService.baseSiteId == BaseCountry.NZ ? 'nzpallet' : 'aupallet'

    this.fullPallet$ = this.productDetailsService.getProductDetailByProductCode(this.item.product?.code).pipe(
      map((product: any) => {
        const pallet = this.getNumberOfUOMperPallet(product.classifications, countryPallet)
        const layer = this.getNumberOfUOMperPalletLayer(product.classifications, countryPallet)

        return {
          numberOfUOMperPallet: parseInt(pallet) || 0,
          numberOfUOMforFullPallet: this.calculateNextFullAmount(parseInt(pallet) || 0, 'isFullPallet'),
          numberOfUOMperLayer: parseInt(layer) || 0,
          numberOfUOMforFullLayer: this.calculateNextFullAmount(parseInt(layer) || 0, 'isFullLayer')
        }
      })
    )
  }

  updateToFullPallet(numberOfUOMforFullPallet: number, currentUOM: number): void {
    if (numberOfUOMforFullPallet > 0) {
      this.quantityControl.setValue(numberOfUOMforFullPallet + currentUOM)
    }
  }

  addPalletLayer(numberOfUOMforFullLayer: number, currentUOM: number): void {
    if (numberOfUOMforFullLayer > 0) {
      this.quantityControl.setValue(numberOfUOMforFullLayer + currentUOM)
    }
  }

  isProductOutOfStock(product: any): any {
    // TODO Move stocklevelstatuses across the app to an enum
    return product && product.stock && product.stock.stockLevelStatus === StockStatus.outOfStock
  }

  removeItem(): void {
    this.itemRemoved.emit(true)
    this.quantityControl.setValue(0)
    this.quantityControl.markAsDirty()
  }

  public get disableOnCancel(): boolean {
    return this.showSpinner
  }

  public isLowStock(product: any): boolean {
    return product?.stock?.stockLevelStatus === StockStatus.lowStock
  }

  public setQuantity(value: number): void {
    this.quantityControl.setValue(value)
    this.quantityControl.markAsDirty()
  }

  public getStockColor(product: ICustomProduct): string {
    switch (true) {
      case product?.stock?.stockLevelStatus === 'inStock':
        return 'in-stock'

      case product?.stock?.stockLevelStatus === 'lowStock':
        return 'low-stock'

      default:
        return 'out-of-stock'
    }
  }

  public getStockIcon(product: ICustomProduct): string {
    switch (true) {
      case product?.stock?.stockLevelStatus === 'inStock':
        return 'fa-check-circle'

      default:
        return 'fa-exclamation-circle'
    }
  }

  private getNumberOfUOMperPallet(classifications: any, countryPallet: string): string {
    return this.filterClassifications(classifications, countryPallet, 'No of UOM per pallet')
  }

  private getNumberOfUOMperPalletLayer(classifications: any, countryPallet: string): string {
    return this.filterClassifications(classifications, countryPallet, 'UOM Per Layer')
  }

  private filterClassifications(
    classifications: any,
    countryPallet: string,
    classificationFeatureName: 'No of UOM per pallet' | 'UOM Per Layer'
  ): string | undefined {
    return classifications
      ?.filter((classification) => classification.code === countryPallet)[0]
      ?.features?.filter((feature) => feature.name === classificationFeatureName)[0]?.featureValues[0]?.value
  }

  private calculateNextFullAmount(unitSize: number, isFullFlag: 'isFullPallet' | 'isFullLayer'): number {
    let nextAmount = unitSize

    if (this.maxQuantity) {
      if (nextAmount + this.quantityControl.value >= this.maxQuantity) {
        nextAmount = this.maxQuantity
      }
    }

    if (this.quantityControl.value > nextAmount) {
      const roundedUp = Math.ceil(this.quantityControl.value / unitSize)
      const calculateNextAmout = roundedUp * unitSize

      nextAmount = !isNaN(calculateNextAmout) ? calculateNextAmout : 0
    }

    if (this.quantityControl.value === nextAmount) {
      this[isFullFlag] = true
    }

    return nextAmount > 0 ? nextAmount - this.quantityControl.value : 0
  }

  /**
   * Don't display fullPallet button on order details page
   */
  public get showFullPallet(): boolean {
    return !this.router.url.includes('order')
  }

  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe()
    }
  }
}
